///
/// EarthBrowser
///
/// Great-circle track utilities for tropical cyclones: geodesic
/// densification (so long chords don't cut under the globe) and
/// time-interpolation of the storm position for clock scrubbing
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { geographic, wgs } from "@/earthbrowser/utils/geographic"
import { trig } from "@stovokor/math/math"
import { dot, normal, type vec3 } from "@stovokor/math/vector"
import type { StormTrackPoint } from "./HurricanesTable"

/** Unit sphere direction for a lat/lon in degrees */
export function unitNormal(latDeg: number, lonDeg: number): vec3 {
	return new geographic(lonDeg * trig.rad, latDeg * trig.rad, 0).normal
}

/** World position (km) on the sphere at an altitude for a unit direction */
export function surfacePoint(unit: vec3, altitudeKm: number): vec3 {
	return unit.multiply(wgs.a + altitudeKm)
}

/**
 * Spherical interpolation between two unit directions. Falls back to a
 * normalized lerp for near-identical or antipodal directions.
 */
export function slerpUnit(a: vec3, b: vec3, f: number): vec3 {
	const cosAngle = Math.max(-1, Math.min(1, dot(a, b)))
	const angle = Math.acos(cosAngle)
	if (angle < 1e-6 || angle > Math.PI - 1e-6) {
		return normal(a.multiply(1 - f).add(b.multiply(f)))
	}
	const sinAngle = Math.sin(angle)
	const wa = Math.sin((1 - f) * angle) / sinAngle
	const wb = Math.sin(f * angle) / sinAngle
	return normal(a.multiply(wa).add(b.multiply(wb)))
}

/**
 * Convert a track to world-space polyline positions, subdividing each
 * segment along the great circle so long chords stay above the sphere.
 * Track points are typically 6-12 hours (often several degrees) apart;
 * PolylineGeometry does no geodesic subdivision of its own.
 *
 * @param maxStepRad - maximum arc per subdivided segment (default ~0.35°)
 */
export function densifiedTrackPositions(
	points: StormTrackPoint[],
	altitudeKm: number,
	maxStepRad: number = 0.006
): vec3[] {
	if (points.length === 0) return []

	const positions: vec3[] = []
	let prev = unitNormal(points[0].lat, points[0].lon)
	positions.push(surfacePoint(prev, altitudeKm))

	for (let i = 1; i < points.length; i++) {
		const next = unitNormal(points[i].lat, points[i].lon)
		const angle = Math.acos(Math.max(-1, Math.min(1, dot(prev, next))))
		const steps = Math.max(1, Math.ceil(angle / maxStepRad))
		for (let s = 1; s <= steps; s++) {
			positions.push(surfacePoint(slerpUnit(prev, next, s / steps), altitudeKm))
		}
		prev = next
	}

	return positions
}

export interface StormStateAtTime {
	lat: number
	lon: number
	wind_kt: number | null
	category: number
	/** True when the clock is outside the storm's known lifetime */
	clamped: boolean
}

/**
 * Interpolate the storm state along its combined observed + forecast track
 * at a clock time. Position follows the great circle between the bracketing
 * points; intensity lerps linearly. Clamps to the track endpoints so
 * scrubbing before genesis or past the forecast horizon parks the storm at
 * the nearest known position.
 */
export function stormStateAtTime(track: StormTrackPoint[], tMs: number): StormStateAtTime | null {
	if (track.length === 0) return null

	const first = track[0]
	if (tMs <= first.t) {
		return { lat: first.lat, lon: first.lon, wind_kt: first.wind_kt, category: first.category, clamped: tMs < first.t }
	}
	const last = track[track.length - 1]
	if (tMs >= last.t) {
		return { lat: last.lat, lon: last.lon, wind_kt: last.wind_kt, category: last.category, clamped: tMs > last.t }
	}

	// Binary search for the bracketing pair
	let lo = 0
	let hi = track.length - 1
	while (hi - lo > 1) {
		const mid = (lo + hi) >> 1
		if (track[mid].t <= tMs) lo = mid
		else hi = mid
	}

	const a = track[lo]
	const b = track[hi]
	const f = (tMs - a.t) / (b.t - a.t)

	const unit = slerpUnit(unitNormal(a.lat, a.lon), unitNormal(b.lat, b.lon), f)
	const geo = new geographic(unit)

	const windA = a.wind_kt
	const windB = b.wind_kt
	const wind = windA !== null && windB !== null
		? windA + (windB - windA) * f
		: (windB ?? windA)

	return {
		lat: geo.latitude * trig.deg,
		lon: geo.longitude * trig.deg,
		wind_kt: wind,
		category: f < 0.5 ? a.category : b.category,
		clamped: false,
	}
}

/**
 * Combined observed + forecast timeline sorted by time, dropping forecast
 * points that overlap the observed record (the TAU=0 forecast point
 * duplicates the latest observed position)
 */
export function combinedTimeline(observed: StormTrackPoint[], forecast: StormTrackPoint[]): StormTrackPoint[] {
	const lastObservedT = observed.length > 0 ? observed[observed.length - 1].t : -Infinity
	return [...observed, ...forecast.filter(p => p.t > lastObservedT)]
}

/**
 * Great-circle destination: lat/lon (degrees) reached from a start point
 * along an initial bearing (radians clockwise from north) for an angular
 * distance (radians)
 */
export function destinationLatLon(
	latDeg: number,
	lonDeg: number,
	bearingRad: number,
	angularDistRad: number
): { lat: number, lon: number } {
	const lat1 = latDeg * trig.rad
	const lon1 = lonDeg * trig.rad
	const sinLat2 = Math.sin(lat1) * Math.cos(angularDistRad)
		+ Math.cos(lat1) * Math.sin(angularDistRad) * Math.cos(bearingRad)
	const lat2 = Math.asin(Math.max(-1, Math.min(1, sinLat2)))
	const lon2 = lon1 + Math.atan2(
		Math.sin(bearingRad) * Math.sin(angularDistRad) * Math.cos(lat1),
		Math.cos(angularDistRad) - Math.sin(lat1) * sinLat2
	)
	return { lat: lat2 * trig.deg, lon: ((lon2 * trig.deg + 540) % 360) - 180 }
}

export interface LandfallPoint {
	lat: number
	lon: number
	/** Estimated landfall time, epoch milliseconds UTC */
	t: number
}

// Landfall search: sample the forecast path roughly every 6 km of arc
const cLandfallStepRad = 0.001

/**
 * Find where the forecast track first crosses from open water onto land,
 * walking the great circle between forecast points and sampling terrain
 * elevation. Returns null when the whole forecast stays at sea, or when the
 * storm never touches water first (already inland).
 *
 * @param elevationAt - terrain sampler returning meters (>0 means land)
 */
export function findLandfall(
	points: StormTrackPoint[],
	elevationAt: (lonRad: number, latRad: number) => number
): LandfallPoint | null {
	if (points.length < 2) return null

	let seenWater = false
	let prevUnit = unitNormal(points[0].lat, points[0].lon)

	const sample = (unit: vec3): boolean => {
		const geo = new geographic(unit)
		return elevationAt(geo.longitude, geo.latitude) > 0
	}

	let prevLand = sample(prevUnit)
	if (!prevLand) seenWater = true

	for (let i = 1; i < points.length; i++) {
		const a = points[i - 1]
		const b = points[i]
		const nextUnit = unitNormal(b.lat, b.lon)
		const angle = Math.acos(Math.max(-1, Math.min(1, dot(prevUnit, nextUnit))))
		const steps = Math.max(1, Math.ceil(angle / cLandfallStepRad))

		for (let s = 1; s <= steps; s++) {
			const f = s / steps
			const unit = slerpUnit(prevUnit, nextUnit, f)
			const land = sample(unit)
			if (land && !prevLand && seenWater) {
				const geo = new geographic(unit)
				return {
					lat: geo.latitude * trig.deg,
					lon: geo.longitude * trig.deg,
					t: a.t + (b.t - a.t) * f,
				}
			}
			if (!land) seenWater = true
			prevLand = land
		}
		prevUnit = nextUnit
	}

	return null
}
